import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Chip, Icon } from '@/components/kit';
import type { IconName } from '@/components/kit';
import { useNarrowViewport, useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { PeriodRow } from './components/PeriodRow';
import {
  amount,
  money,
  thisPeriod,
  useAccountsSummary,
  useReceivables,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';

/**
 * Accounts home — `g_home` (1). THE READ LAYER WITH EXPORT RIGHTS.
 *
 * Four figures and nine doors, over one month and every branch. The header
 * says "read-only" because that is the honest description of this whole
 * surface: the accountant reconciles and files, and the money is moved at the
 * desk, in the payout run and by the manager who approves it.
 *
 * WHY THESE FOUR. Collected is what actually arrived, not what was billed —
 * a clinic's August is the money that landed in August. The VAT inside it is
 * prorated, because half-paid invoices do not owe the authority their whole
 * tax yet. Refunds are what went back out. Outstanding is deliberately NOT
 * period-scoped: it is what is owed right now, whenever it was billed, since
 * a figure that dropped last quarter's unpaid claim the day the quarter
 * turned would hide the one thing worth looking at.
 */

interface Door {
  key: string;
  icon: IconName;
  title: string;
  sub: string;
  to?: string;
  /**
   * The board's own three groups — money in, money out, and what the two add
   * up to. On the desk the RAIL carries them, so the grid stays a plain grid;
   * on a phone there is no rail and the doors are one per row, which is a
   * list of nine with nothing to break it, so `g_a1` puts the headings back.
   */
  group: 'Money in' | 'Money out' | 'The books';
}

export function AccountsHomePage() {
  const go = useNavigate();
  const [period, setPeriod] = useState(thisPeriod);
  const narrow = useNarrowViewport();
  const { data, isLoading, isError } = useAccountsSummary(period);
  const { data: receivables } = useReceivables();
  const [query, setQuery] = useState('');

  usePageMeta({ title: 'Accounts' });

  const patients = receivables?.find((r) => r.kind === 'patient')?.owedMinor ?? 0;
  const insurers = (receivables ?? [])
    .filter((r) => r.kind === 'insurer')
    .reduce((sum, r) => sum + r.owedMinor, 0);

  const doors: Door[] = [
    { key: 'bills', group: 'Money in', icon: 'file-invoice', title: 'Bills & claims', sub: 'the billing suite, read-only', to: '/accountant/bills' },
    {
      key: 'receivables',
      group: 'Money in',
      icon: 'clock-dollar',
      title: 'Receivables',
      sub: receivables?.length
        ? `debts ${amount(patients)} · insurers ${amount(insurers)}`
        : 'what is owed to the clinic',
      to: '/accountant/receivables',
    },
    { key: 'expenses', group: 'Money out', icon: 'receipt-2', title: 'Expenses', sub: 'rent · consumables · subscriptions', to: '/accountant/expenses' },
    { key: 'people', group: 'Money out', icon: 'users-group', title: 'People we pay', sub: 'doctors owed · payroll · statements', to: '/accountant/people' },
    { key: 'payables', group: 'Money out', icon: 'building-store', title: 'Payables', sub: 'what we owe, and by when', to: '/accountant/payables' },
    { key: 'tax', group: 'The books', icon: 'receipt-tax', title: 'Tax report', sub: 'exempt / taxable split', to: '/accountant/tax' },
    { key: 'pnl', group: 'The books', icon: 'scale', title: 'Profit & loss', sub: 'collected against spent', to: '/accountant/pnl' },
    { key: 'reports', group: 'The books', icon: 'chart-bar', title: 'Financial reports', sub: 'revenue · insurance · charts · trends', to: '/accountant/reports' },
    { key: 'export', group: 'The books', icon: 'download', title: 'Export center', sub: 'the monthly close package', to: '/accountant/export' },
  ];

  useShellChrome(
    {
      raw: true,
      phoneMenu: (close) => <AccountantMenu door="home" onClose={close} />,
      title: 'Accounts',
      sub: (
        <>
          the read layer with export rights{' '}
          <Chip kind="outline" small>read-only</Chip>
        </>
      ),
      search: 'Search bills, people, invoices…',
      searchValue: query,
      onSearchChange: setQuery,
    },
    [query],
  );

  return (
    <div className="maie desk acc-root">
      <PeriodRow period={period} onChange={setPeriod} branches={data?.branches} />

      <div className="dcontent scroll">
        <div className="acc-stats">
          <Stat
            label="Revenue — collected"
            value={isLoading ? '…' : money(data?.collectedMinor ?? 0)}
            sub={data?.billed ? `${data.billed} bills raised this month` : 'nothing billed yet'}
          />
          <Stat
            label="VAT collected"
            value={isLoading ? '…' : money(data?.vatMinor ?? 0)}
            sub="prorated to what was actually paid"
          />
          <Stat
            label="Refunds"
            value={isLoading ? '…' : money(data?.refundedMinor ?? 0)}
            sub={data?.refundedMinor ? 'voided bills that had taken money' : 'nothing went back out'}
            tone={data?.refundedMinor ? 'amber' : undefined}
          />
          <Stat
            label="Outstanding"
            value={isLoading ? '…' : money(data?.outstandingMinor ?? 0)}
            sub="owed right now — not just this month"
            tone={data?.outstandingMinor ? 'amber' : undefined}
          />
        </div>

        {isError ? (
          <Card>
            <p className="t-sec mut">The month did not come back. Nothing has been changed.</p>
          </Card>
        ) : null}

        {(() => {
          const shown = doors.filter(
            (d) => !query.trim() || `${d.title} ${d.sub}`.toLowerCase().includes(query.trim().toLowerCase()),
          );
          const door = (d: Door) => (
            <button
              key={d.key}
              type="button"
              className="acc-door"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                textAlign: 'left',
                background: 'none',
                border: 0,
                borderBottom: '1px solid var(--color-border-subtle, var(--border-100))',
                padding: '14px 8px',
                cursor: 'pointer',
              }}
              onClick={() => d.to && go(d.to)}
              disabled={!d.to}
            >
              <span className="acc-door-icon" aria-hidden="true">
                <Icon name={d.icon} />
              </span>
              <span className="grow">
                <p className="t-strong">{d.title}</p>
                <p className="t-cap mut">{d.sub}</p>
              </span>
              <Icon name="chevron-right" />
            </button>
          );
          if (narrow) {
            return (
              <div className="acc-doors">
                {shown.map((d, i) => (
                  <Fragment key={d.key}>
                    {shown[i - 1]?.group !== d.group ? <p className="t-sec mut acc-group">{d.group}</p> : null}
                    <button
                      type="button"
                      className="card acc-door"
                      onClick={() => d.to && go(d.to)}
                    >
                      <span className="acc-door-icon" aria-hidden="true">
                        <Icon name={d.icon} />
                      </span>
                      <span className="grow">
                        <p className="t-strong">{d.title}</p>
                        <p className="t-cap mut">{d.sub}</p>
                      </span>
                    </button>
                  </Fragment>
                ))}
              </div>
            );
          }
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 24, alignItems: 'start' }}>
              {(['Money in', 'Money out', 'The books'] as const).map((g) => (
                <div key={g}>
                  <p className="t-ovl mut" style={{ padding: '0 8px 10px', borderBottom: '1px solid var(--color-border-subtle, var(--border-100))' }}>{g}</p>
                  {shown.filter((d) => d.group === g).map(door)}
                </div>
              ))}
            </div>
          );
        })()}

        <p className="t-sec mut mt12">
          Every view and every export is logged — who pulled what, when.
        </p>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'amber';
}) {
  return (
    <div className="card" style={{ margin: 0 }}>
      <p className="t-ovl mut">{label}</p>
      <p className={`t-dxl mt8${tone === 'amber' ? ' amb' : ''}`}>{value}</p>
      <p className="t-cap mut mt4">{sub}</p>
    </div>
  );
}
